import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { QueryStatus } from '@reduxjs/toolkit/query';

import {
	transactionApi,
	useWithdrawalRequestCreateMutation,
	useWithdrawalRequestRetrieveQuery,
	WithdrawalRequestStatusEnum,
} from 'entities/Transaction';
import { commonApi, useAppConfigRetrieveQuery, useMetaRetrieveQuery } from 'shared/api';
import { useMedia } from 'shared/hooks';
import { EBaseApiTags } from 'shared/typings';

import { TRANSACTION_STATUS_POLLING_INTERVAL } from '../consts';

export const useBalance = () => {
	const { t } = useTranslation();
	const isMobile = useMedia();
	const dispatch = useDispatch();
	const interval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const { data: appConfig } = useAppConfigRetrieveQuery();
	const { data: metaData } = useMetaRetrieveQuery();

	const [addTransaction, { isLoading, status: createdStatus, data: createdWithdrawal }] =
		useWithdrawalRequestCreateMutation();

	const { data: checkedWithdrawal } = useWithdrawalRequestRetrieveQuery(createdWithdrawal?.uuid as string, {
		skip: !createdWithdrawal?.uuid || !modalIsOpen,
	});

	const onSuccessAddingTransaction = useEffectEvent(() => {
		setModalIsOpen(true);
		dispatch(commonApi.util.invalidateTags([EBaseApiTags.CONFIG]));

		interval.current = setInterval(() => {
			dispatch(transactionApi.util.invalidateTags([EBaseApiTags.TRANSACTION]));
		}, TRANSACTION_STATUS_POLLING_INTERVAL);
	});

	useEffect(() => {
		if (createdStatus === QueryStatus.fulfilled) {
			onSuccessAddingTransaction();
		}
	}, [createdStatus]);

	useEffect(() => {
		if (
			!modalIsOpen ||
			checkedWithdrawal?.status === WithdrawalRequestStatusEnum.Success ||
			checkedWithdrawal?.status === WithdrawalRequestStatusEnum.Failed
		) {
			clearInterval(interval.current);
			dispatch(transactionApi.util.invalidateTags([EBaseApiTags.TRANSACTION_LIST]));
		}
	}, [modalIsOpen, checkedWithdrawal?.status, dispatch]);

	return {
		t,
		isMobile,
		modalIsOpen,
		appConfig,
		currenciesMap: metaData?.currenciesMap || {},
		addTransaction,
		isLoading,
		checkedWithdrawal,
		setModalIsOpen,
	};
};
